import createClient, { type Middleware } from "openapi-fetch";

import type { paths } from "./schema";

/** 中台返回的 RFC 9457 problem+json。`code` 是机器可读的稳定错误码。 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, detail: string) {
    super(detail || code);
    this.status = status;
    this.code = code;
  }

  get isAuth(): boolean {
    return this.status === 401;
  }
}

function readCookie(name: string): string {
  const hit = document.cookie.split("; ").find((c) => c.startsWith(`${name}=`));
  return hit ? decodeURIComponent(hit.slice(name.length + 1)) : "";
}

const csrf: Middleware = {
  onRequest({ request }) {
    if (!["GET", "HEAD", "OPTIONS"].includes(request.method)) {
      request.headers.set("x-csrf-token", readCookie("fx_csrf"));
    }
    return request;
  },
};

export const api = createClient<paths>({ baseUrl: "", credentials: "include" });
api.use(csrf);

type Result<T> = { data?: T; error?: unknown; response: Response };

/** 把 openapi-fetch 的结果变成"成功返回数据，失败抛 ApiError"，供 TanStack Query 使用。 */
export async function unwrap<T>(promise: Promise<Result<T>>): Promise<T> {
  let result: Result<T>;
  try {
    result = await promise;
  } catch {
    throw new ApiError(0, "NETWORK", "无法连接中台，请检查网络");
  }
  const { data, error, response } = result;
  if (response.ok) return data as T;
  const problem = (error ?? {}) as { code?: string; detail?: string; title?: string };
  throw new ApiError(response.status, problem.code ?? `HTTP_${response.status}`, problem.detail || problem.title || "");
}

/** 面向用户的错误文案：优先使用中台给出的中文说明。 */
export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return "发生未知错误";
}
